import React, { useState, useRef } from "react";
import { closeIcon, editIcon } from "../../Icons/Icons";
import AddSalaryComponent from "./AddSalaryComponent";
import dayjs from "dayjs";
import EmployeeSalaryComponentPane from "./EmployeeSalaryComponentPane";

import {
  useViewEmployeePayrollDetails,
  useGenerateEmployeePayrollPolicy,
  usePublishEmployeePayrollPolicy,
  useGetEmployeePolicyPayrollList,
} from "../../queries/payrollQuries";
import { toast } from "react-toastify";
import {
  PAY_SLIP_COMPONENT_TYPE,
  PAYROLL_STATUS,
  SALARY_COMPONENT_TYPES,
  SALARY_COMPONENT_CALCULATION,
} from "../../redux/constants/payrollComponents";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import "react-sliding-pane/dist/react-sliding-pane.css";
import SlidingPane from "react-sliding-pane";
const EmployeePayslipData = ({
  employee,
  onClose,
  makeApiCall,
  handleGeneratePayslip,
  source,
  month,
  year,
}) => {
  const {
    data: response,
    isLoading,
    isError,
  } = useViewEmployeePayrollDetails({
    employee_id: employee.employee_id,
    month: month,
    year: year,
  });
  const [confirmAction, setConfirmAction] = useState(null); // "GENERATE" or "PUBLISH"
  const [isPaneOpen, setIsPaneOpen] = useState(false);
  const [editData, setEditData] = useState(null);
  const [isCancelled, setIsCancelled] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showBankConfirmModal, setShowBankConfirmModal] = useState(false);
  const [showProgressModal, setShowProgressModal] = useState(false);
  const cancelTimeoutRef = useRef(null);
  const [progressMessage, setProgressMessage] = useState("");
  const axiosPrivate = useAxiosPrivate();

  const generatePayrollMutation = useGenerateEmployeePayrollPolicy();
  const publishPayrollMutation = usePublishEmployeePayrollPolicy();
  const payrollData = response?.data;

  const handleGenerate = () => {
    if (!employee?.account_number?.trim()) {
      setShowBankConfirmModal(true);
    } else {
      setShowConfirmModal(true);
      setConfirmAction(PAYROLL_STATUS.GENERATED);
    }
  };

  const handlePublish = () => {
    if (!employee?.account_number?.trim()) {
      setShowBankConfirmModal(true);
    } else {
      setShowConfirmModal(true);
      setConfirmAction(PAYROLL_STATUS.PUBLISHED);
    }
  };
  // Generate Payroll
  const confirmAndGeneratePayroll = () => {
    setProgressMessage("Generating payslip...");
    setShowProgressModal(true);
    setIsCancelled(false);
    setShowConfirmModal(false);
    setShowBankConfirmModal(false);
    const payload = {
      payroll_policy_id: payrollData?.payroll_policy_id,
      employees: [{ employee_id: employee.employee_id }],
      month: month,
      year: year,
    };
    generatePayrollMutation.mutate(payload, {
      onSuccess: (res) => {
        cancelTimeoutRef.current = setTimeout(() => {
          if (isCancelled) return;
          setShowProgressModal(false);
          if (res?.status) {
            toast.success(res?.message || "Payslip generated successfully");
            handleGeneratePayslip?.(employee?.employee_id);
          } else {
            toast.error(res?.error || "Something went wrong");
          }
        }, 3000);
      },
      onError: (err) => {
        setShowProgressModal(false);
        toast.error(
          err?.response?.data?.message || "Failed to generate payslip"
        );
      },
    });
  };
  // Publish Payroll
  const confirmAndPublishPayroll = () => {
    setProgressMessage("Publishing payroll...");
    setShowProgressModal(true);
    setIsCancelled(false);
    setShowConfirmModal(false);
    setShowBankConfirmModal(false);

    const payload = {
      payroll_policy_id: payrollData?.payroll_policy_id,
      employees: [{ employee_id: employee.employee_id }],
      month: month,
      year: year,
    };

    publishPayrollMutation.mutate(payload, {
      onSuccess: (res) => {
        cancelTimeoutRef.current = setTimeout(() => {
          if (isCancelled) return;
          setShowProgressModal(false);
          if (res?.status) {
            toast.success(res?.message || "Payroll published successfully");
            handleGeneratePayslip?.(employee?.employee_id);
          } else {
            toast.error(
              res?.message || "Something went wrong while publishing"
            );
          }
        }, 3000);
      },
      onError: (err) => {
        setShowProgressModal(false);
        toast.error(
          err?.response?.data?.message || "Failed to publish payroll"
        );
      },
    });
  };
  // Cancel Progress
  const handleCancelProgress = () => {
    setIsCancelled(true);
    setShowProgressModal(false);
    if (cancelTimeoutRef.current) clearTimeout(cancelTimeoutRef.current);
  };

  // Open add pane
  const handleAddClick = () => {
    setEditData(null);
    setIsPaneOpen(true);
  };

  // Open edit pane
  const handleEditRow = (componentName) => {
    const selected = payrollData.employee_payroll_calculated_data?.find(
      (item) => item.component_name === componentName
    );
    if (selected) {
      setEditData(selected);
      setIsPaneOpen(true);
    }
  };

  // Close pane
  const handleClosePane = () => {
    setIsPaneOpen(false);
    setEditData(null);
  };

  const earnings =
    payrollData?.employee_payroll_calculated_data?.filter(
      (item) =>
        Number(item.component_type) === Number(SALARY_COMPONENT_TYPES.EARNING)
    ) ?? [];

  const deductions =
    payrollData?.employee_payroll_calculated_data?.filter(
      (item) =>
        Number(item.component_type) === Number(SALARY_COMPONENT_TYPES.DEDUCTION)
    ) ?? [];
  const tds =
    payrollData?.employee_payroll_calculated_data?.filter(
      (item) =>
        Number(item.component_type) === Number(SALARY_COMPONENT_TYPES.TDS)
    ) ?? [];

  const contributions =
    payrollData?.employee_payroll_calculated_data?.filter(
      (item) =>
        Number(item.component_type) ===
        Number(SALARY_COMPONENT_TYPES.CONTRIBUTIONS)
    ) ?? [];

  const netSalary = payrollData?.employee_payroll_calculated_data?.find(
    (item) => item.component_name === PAY_SLIP_COMPONENT_TYPE.NET_PAY
  );
  const attendanceComponent =
    payrollData?.employee_payroll_calculated_data?.find(
      (item) => item.component_name === PAY_SLIP_COMPONENT_TYPE.ATTENDANCE
    );
  const isPending = payrollData?.payroll_status === PAYROLL_STATUS.PENDING;
  const totalEarnings = isPending
    ? 0
    : earnings?.reduce(
        (sum, curr) => sum + Number(curr?.calculated_value || 0),
        0
      );
  const totalDeductions = isPending
    ? 0
    : deductions?.reduce(
        (sum, curr) => sum + Number(curr?.calculated_value || 0),
        0
      );
  if (isLoading) {
    return <div className="p-6 text-center">Loading payroll data...</div>;
  }

  if (isError) {
    return (
      <div className="p-6 text-center text-red-500">
        <button
          onClick={onClose}
          className="bg-white text-blue-600 px-4 py-1 border rounded"
        >
          {closeIcon}
        </button>
        Failed to load payroll data.
      </div>
    );
  }

  if (!payrollData?.payroll_policy_id) {
    return (
      <div className="p-6 text-center">
        <button onClick={onClose} className="text-blue-600 float-right">
          {closeIcon}
        </button>
        <p className="text-red-500">
          No payroll policy assigned to this employee.
        </p>
      </div>
    );
  }
  // DOWNLOAD PAYSLIP
  const handleDownloadPayslip = async (employee, month, year) => {
    try {
      const response = await axiosPrivate.get(
        "/payroll/generateEmployeePayslip",
        {
          params: {
            employee_id: employee.employee_id,
            month: month,
            year: year,
          },
          responseType: "blob",
        }
      );

      if (response?.status === 200) {
        const formattedMonth = dayjs(`${year}-${month}-01`).format("MMMM");
        const firstName = (employee.first_name || "")
          .trim()
          .replace(/\s+/g, "_");
        const lastName = (employee.last_name || "").trim().replace(/\s+/g, "_");
        const employeeName = [firstName, lastName].filter(Boolean).join("_");

        const fileName = `Payslip ${employeeName} ${formattedMonth} ${year}.pdf`;

        // Create blob and trigger download
        const blob = new Blob([response.data], {
          type: response.headers["content-type"] || "application/pdf",
        });
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = downloadUrl;
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(downloadUrl);

        toast.success(
          `Payslip for ${formattedMonth} ${year} downloaded successfully`
        );
      } else {
        toast.error("Error while downloading the payslip");
      }
    } catch (error) {
      toast.error("Failed to download the payslip. Please try again later.");
    }
  };

  return (
    <>
      <div className="p-4 max-w-4xl mx-auto">
        <div className="bg-white rounded shadow border">
          <div className="bg-blue-600 text-white px-6 py-4 flex justify-between items-center">
            <h2 className="text-lg font-semibold">Employee Payroll Details</h2>
            <div className="flex gap-2">
              {payrollData?.payroll_status !== PAYROLL_STATUS.PUBLISHED && (
                <button
                  onClick={handleAddClick}
                  className="bg-white text-blue-600 px-4 py-1 border rounded"
                >
                  Add Component
                </button>
              )}

              <SlidingPane
                isOpen={isPaneOpen}
                title={
                  editData
                    ? "Edit Employee Salary Component"
                    : "Add Employee Salary Component"
                }
                from="right"
                width="450px"
                onRequestClose={handleClosePane}
                parentSelector={() => document.body}
                className="!z-[9999]"
                overlayClassName="!z-[9998]"
              >
                <EmployeeSalaryComponentPane
                  closePane={handleClosePane}
                  employee={employee}
                  month={month}
                  year={year}
                  makeApiCall={makeApiCall}
                  editData={editData}
                  isEditMode={!!editData}
                />
              </SlidingPane>

              <button
                onClick={onClose}
                className="bg-white text-blue-600 px-4 py-1 border rounded"
              >
                {closeIcon}
              </button>
            </div>
          </div>

          <div className="px-6 py-4 border-b">
            <p>
              Employee: {employee?.first_name} {employee?.last_name} (
              {employee?.employee_serial}) | Payroll policy:{" "}
              {payrollData?.policy_name} | Status:{" "}
              {payrollData?.payroll_status || "N/A"}
            </p>
          </div>
          {!isPending && (
            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
              <div>
                <h3 className="font-semibold mb-2">EARNINGS</h3>
                <table className="w-full">
                  <tbody>
                    {earnings.map(
                      ({
                        component_name,
                        calculated_value,
                        payroll_status,
                        calculation_type,
                      }) => (
                        <Row
                          key={component_name}
                          label={component_name}
                          value={calculated_value}
                          status={payroll_status}
                          type={calculation_type}
                          onEdit={handleEditRow}
                        />
                      )
                    )}
                  </tbody>
                </table>
              </div>
              <div>
                <h3 className="font-semibold mb-2">DEDUCTIONS</h3>
                <table className="w-full">
                  <tbody>
                    {deductions.map(
                      ({
                        component_name,
                        calculated_value,
                        payroll_status,
                        calculation_type,
                      }) => (
                        <Row
                          key={component_name}
                          label={component_name}
                          value={calculated_value}
                          status={payroll_status}
                          type={calculation_type}
                          onEdit={handleEditRow}
                        />
                      )
                    )}
                  </tbody>
                </table>
              </div>
              {tds.length > 0 && (
                <div>
                  <h3 className="font-semibold mb-2">TDS</h3>
                  <table className="w-full">
                    <tbody>
                      {tds.map(
                        ({
                          component_name,
                          calculated_value,
                          payroll_status,
                          calculation_type,
                        }) => (
                          <Row
                            key={component_name}
                            label={component_name}
                            value={calculated_value}
                            status={payroll_status}
                            type={calculation_type}
                            onEdit={handleEditRow}
                          />
                        )
                      )}
                    </tbody>
                  </table>
                </div>
              )}

              <div>
                <h3 className="font-semibold mb-2">CONTRIBUTIONS</h3>
                <table className="w-full">
                  <tbody>
                    {contributions.map(
                      ({ component_name, calculated_value }) => (
                        <Row
                          key={component_name}
                          label={component_name}
                          value={calculated_value}
                        />
                      )
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="bg-gray-100 px-6 py-4">
            {!isPending && (
              <div className="text-sm">
                <p>Total Earnings: ₹ {totalEarnings.toFixed(2)}</p>
                <p>Total Deductions: ₹ {totalDeductions.toFixed(2)}</p>
                <p className="text-lg font-semibold">
                  Net Pay: ₹ {netSalary?.calculated_value || "0.00"}
                </p>
                <p className="text-xs">
                  Days Worked:{" "}
                  {attendanceComponent?.calculated_value
                    ? Math.round(
                        parseFloat(attendanceComponent.calculated_value)
                      )
                    : "N/A"}
                </p>
              </div>
            )}
          </div>
        </div>

        <div className="mt-6 text-center flex gap-4 justify-center">
          {/* Generate / Re-generate Payroll */}
          {[PAYROLL_STATUS.PENDING, PAYROLL_STATUS.GENERATED].includes(
            payrollData?.payroll_status
          ) && (
            <button
              onClick={handleGenerate}
              disabled={generatePayrollMutation.isLoading}
              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
            >
              {generatePayrollMutation.isLoading
                ? "Processing..."
                : payrollData?.payroll_status === PAYROLL_STATUS.GENERATED
                ? "Re-generate Payroll"
                : "Generate Payroll"}
            </button>
          )}

          {/* Publish Payroll */}
          {payrollData?.payroll_status === PAYROLL_STATUS.GENERATED && (
            <button
              onClick={handlePublish}
              disabled={publishPayrollMutation.isLoading}
              className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
            >
              {publishPayrollMutation.isLoading
                ? "Processing..."
                : "Publish Payroll"}
            </button>
          )}
          {payrollData?.payroll_status === PAYROLL_STATUS.PUBLISHED && (
            <button
              onClick={() => handleDownloadPayslip(employee, month, year)}
              className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
            >
              Download Payslip
            </button>
          )}
        </div>

        {/* Info message */}
        {payrollData?.payroll_status !== PAYROLL_STATUS.PUBLISHED && (
          <p className="mt-2 text-xs text-red-500 text-center">
            {payrollData?.payroll_status === PAYROLL_STATUS.GENERATED
              ? "Edit if needed and click Re-generate or Publish Payroll to finalize."
              : "Click on Generate Payroll to proceed."}
          </p>
        )}
      </div>

      {showConfirmModal && (
        <Modal
          title={
            confirmAction === PAYROLL_STATUS.GENERATED
              ? "Generate Payroll"
              : "Publish Payroll"
          }
          message={
            confirmAction === PAYROLL_STATUS.GENERATED ? (
              <div>
                <span>
                  Are you sure you want to{" "}
                  {payrollData?.payroll_status === PAYROLL_STATUS.GENERATED
                    ? "re-generate"
                    : "generate"}{" "}
                  payroll for {employee?.first_name} {employee?.last_name}?
                </span>
                <span className="block text-red-600 font-semibold mt-2">
                  WARNING: Changes may affect previously generated payroll data.
                </span>
              </div>
            ) : (
              <div>
                <span>
                  Are you sure you want to publish payroll for{" "}
                  {employee?.first_name} {employee?.last_name}?
                </span>
                <span className="block text-red-600 font-semibold mt-2">
                  WARNING: This action cannot be undone.
                </span>
                <span className="block mt-1 text-red-600">Once published:</span>
                <ul className="list-disc pl-6 mt-2 text-gray-600">
                  <li>Payroll data cannot be modified</li>
                  <li>Payslips will be viewable by employees</li>
                </ul>
              </div>
            )
          }
          onCancel={() => setShowConfirmModal(false)}
          onConfirm={
            confirmAction === PAYROLL_STATUS.GENERATED
              ? confirmAndGeneratePayroll
              : confirmAndPublishPayroll
          }
          disableConfirm={false}
        />
      )}

      {showBankConfirmModal && (
        <Modal
          message="No bank details available. Payroll cannot be generated. Please fill in the employee's bank details first."
          onCancel={() => setShowBankConfirmModal(false)}
          disableConfirm={true}
        />
      )}

      {showProgressModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 z-50">
          <div className="bg-white p-6 rounded shadow w-80 text-center">
            <p className="text-blue-600 mb-4">{progressMessage}</p>
            <div className="h-2 bg-gray-200 rounded overflow-hidden">
              <div
                className="bg-blue-500 h-full animate-pulse"
                style={{ width: "100%" }}
              ></div>
            </div>
            <button
              onClick={handleCancelProgress}
              className="mt-2 text-sm text-red-500 hover:underline"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </>
  );
};

const Row = ({ label, value, onEdit, status, type }) => (
  <tr className="border-t group hover:bg-gray-50">
    <td className="p-2 flex justify-between items-center">
      <span>{label}</span>
      {status !== PAYROLL_STATUS.PUBLISHED &&
        (Number(type) === SALARY_COMPONENT_CALCULATION.FIXED_AMOUNT ||
          Number(type) === SALARY_COMPONENT_CALCULATION.PERCENTAGE) && (
          <button
            onClick={() => onEdit(label)}
            className="ml-2 text-blue-600 opacity-0 group-hover:opacity-100"
          >
            {editIcon}
          </button>
        )}
    </td>
    <td className="p-2 text-right">
      {!value || parseFloat(value) === 0
        ? "Not Applicable"
        : Number(value).toFixed(2)}
    </td>
  </tr>
);

const Modal = ({
  title,
  message,
  onCancel,
  onConfirm,
  disableConfirm = false,
}) => (
  <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 z-50">
    <div className="bg-white p-6 rounded shadow-lg w-full max-w-md">
      {title && <h3 className="text-lg font-semibold mb-4">{title}</h3>}
      <div>{message}</div>
      <div className="flex justify-end gap-2 mt-4">
        <button
          onClick={onCancel}
          className="px-4 py-2 border rounded text-gray-600"
        >
          Close
        </button>
        {!disableConfirm && (
          <button
            onClick={onConfirm}
            className="px-4 py-2 bg-blue-600 text-white rounded"
          >
            OK
          </button>
        )}
      </div>
    </div>
  </div>
);

export default EmployeePayslipData;
